// Purpose: Provide standardized classification for messages received from the server
// over WebSocket, building on top of parsePayloadFromServer.

import { parsePayloadFromServer } from './util.payload.from.server';

/**
 * Message types for classifying server-originating WebSocket payloads.
 */
const MESSAGE_TYPES = {
    ERROR: 'ERROR',
    CONNECTED: 'CONNECTED',
    DISCONNECTED: 'DISCONNECTED',
    REQUEST_CONFIRMATION: 'REQUEST_CONFIRMATION',
    MESSAGE: 'MESSAGE',
};

const UTIL_SERVER_WS_MESSAGES = {
    MESSAGE_TYPES,

    // Re-export parser to keep parsing & classifying together.
    parsePayloadFromServer,

    /**
     * Determines the message type of a given payload from the server.
     *
     * Classification Rules:
     * - If payload includes an `error`, returns ERROR.
     * - If body is an object → MESSAGE (application-level).
     * - If body is a string:
     *    - "Connected." → CONNECTED
     *    - "Disconnected." → DISCONNECTED
     *    - starts with "Received your message:" → REQUEST_CONFIRMATION
     * - Otherwise falls back to MESSAGE
     *
     * @param {Object} payloadFromServer - Raw payload from the server.
     * @returns {string} One of MESSAGE_TYPES
     */
    getMessageType: (payloadFromServer) => {
        const { error, body } = parsePayloadFromServer(payloadFromServer);

        if (error) {
            return MESSAGE_TYPES.ERROR;
        }

        if (typeof body === 'object') {
            return MESSAGE_TYPES.MESSAGE;
        }

        // ignorable message types
        if (typeof body === 'string') {
            if (body === 'Connected.') {
                return MESSAGE_TYPES.CONNECTED;
            }
            if (body === 'Disconnected.') {
                return MESSAGE_TYPES.DISCONNECTED;
            }
            if (body.slice(0, 22) === 'Received your message:') {
                return MESSAGE_TYPES.REQUEST_CONFIRMATION;
            }
        }

        return MESSAGE_TYPES.MESSAGE;    
    },

    /**
     * Determines whether a server message is considered "internal"
     * (should usually be handled at framework level rather than passed
     * to application logic).
     *
     * By default, CONNECTED, DISCONNECTED, and REQUEST_CONFIRMATION are
     * classified as internal.
     *
     * @param {Object} payloadFromServer - Raw payload from the server.
     * @returns {boolean} true if internal, false otherwise
     */
    defaultFuncIsInternalMessage: (payloadFromServer) => {
        const { getMessageType } = UTIL_SERVER_WS_MESSAGES;
        const internalMessageTypes = [
            MESSAGE_TYPES.CONNECTED, 
            MESSAGE_TYPES.DISCONNECTED, 
            MESSAGE_TYPES.REQUEST_CONFIRMATION
        ];

        const messageType = getMessageType(payloadFromServer);
        return internalMessageTypes.indexOf(messageType) > -1;
    }    
}

export default UTIL_SERVER_WS_MESSAGES;